import LinkIcon from '@material-ui/icons/LaunchRounded';
import LockIcon from '@svg/lock.svg';
import React from 'react';
import styled from 'styled-components';
import { cardBorder } from '../styles/card';
import { focusOutline } from '../styles/focus';
import { IconButtonLink } from './Button/IconButton';
import { ActionMenu } from './menus/ActionMenu';
import { Tooltip } from './Tooltip';
import { VisuallyHidden } from './VisuallyHidden';

const Container = styled.li`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  max-width: 38rem;
  margin: 0.5rem 0;
  ${cardBorder}
`;

const Outline = styled.div<{
  $isDisabled: boolean;
  $isLocked: boolean;
  $hasRightAdornment: boolean;
}>`
  position: relative;
  display: flex;
  flex: 1 0 100%;
  flex-flow: row;
  align-items: center;
  padding: 0.625rem
    ${({ $hasRightAdornment }) => ($hasRightAdornment ? '3rem' : '0.5rem')}
    0.5rem 0.5rem;
  overflow: hidden;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.gray333};
  ${({ $isLocked, theme }) =>
    $isLocked ? `background-color: ${theme.colors.grayeee}` : ''}
  ${({ $isDisabled, theme }) =>
    $isDisabled
      ? `background-color: ${theme.colors.grayf6}; opacity: 0.95;`
      : ''}
`;

const IconTooltip = styled(Tooltip)`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  margin-right: 1.2rem;

  & svg {
    width: 1rem;
    height: 1rem;
    fill: ${({ theme }) => theme.colors.gray333};
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
`;

const ContentText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledIconButtonLink = styled(IconButtonLink)`
  height: 1rem;
  padding: 0;
  margin-left: 0.5rem;
  font-size: 1rem;
`;

const Qualifier = styled.div`
  flex: 0 0 auto;
  padding-left: 1rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.gray666};
`;

const MenuContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.colors.grayddd};
`;

const Lock = styled(MenuContainer)`
  & svg {
    height: 1rem;
    color: ${({ theme }) => theme.colors.gray666};
  }

  &:focus-visible {
    ${focusOutline({ $outlineOffset: '0' })}
  }
`;

const AdditionalInfo = styled.div`
  flex: 1 0 100%;
  padding: 0.625rem 0.5rem 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.gray333};
  border-top: 1px solid ${({ theme }) => theme.colors.grayddd};
`;

type ListItemCardProps = {
  actionMenuAltText?: string;
  actionMenuOptions?: React.ComponentProps<typeof ActionMenu>['options'];
  additionalInfo?: React.ReactElement | string;
  className?: string;
  content: string | React.ReactNode;
  icon?: React.ReactElement;
  iconTooltip?: string;
  isDisabled?: boolean;
  isLocked?: boolean;
  link?: {
    to: string;
    title: string;
  };
  lockedIconTooltip?: string;
  outerElement?: React.ElementType | keyof JSX.IntrinsicElements;
  qualifier?: string | React.ReactNode;
};

export function ListItemCard({
  actionMenuAltText,
  actionMenuOptions,
  additionalInfo,
  className,
  content,
  icon,
  iconTooltip,
  isDisabled = false,
  isLocked = false,
  link,
  lockedIconTooltip,
  outerElement = 'div',
  qualifier,
}: ListItemCardProps) {
  return (
    <Container className={className} as={outerElement}>
      <Outline
        $isDisabled={isDisabled}
        $isLocked={isLocked}
        $hasRightAdornment={!!actionMenuOptions?.length || isLocked}
      >
        {!!icon && !!iconTooltip && (
          <IconTooltip title={iconTooltip}>
            <div>{icon}</div>
          </IconTooltip>
        )}

        <Content>
          <ContentText>{content}</ContentText>
          {link && (
            <Tooltip title={link.title} placement="right">
              <StyledIconButtonLink color="primary" to={link.to}>
                <VisuallyHidden>{link.title}</VisuallyHidden>
                <LinkIcon />
              </StyledIconButtonLink>
            </Tooltip>
          )}
        </Content>

        {qualifier && <Qualifier>{qualifier}</Qualifier>}

        {!!actionMenuOptions?.length && !isLocked && (
          <MenuContainer>
            <ActionMenu
              altText={actionMenuAltText || `Action menu`}
              options={actionMenuOptions}
              isDisabled={isDisabled}
            />
          </MenuContainer>
        )}
        {isLocked &&
          (lockedIconTooltip ? (
            <Tooltip placement="bottom" tabIndex={0} title={lockedIconTooltip}>
              <Lock>
                <LockIcon />
              </Lock>
            </Tooltip>
          ) : (
            <Lock>
              <LockIcon />
            </Lock>
          ))}
      </Outline>
      {!!additionalInfo && <AdditionalInfo>{additionalInfo}</AdditionalInfo>}
    </Container>
  );
}
